import { UseQueryOptions, useQuery } from '@tanstack/react-query';

import {
  useAwsApiGatewayEnnismoreApiClientConfig,
  useKongEnnismoreApiClientConfig,
} from '@/api';
import { useLocale } from '@/i18n';
import { trpc } from '@/trpc/client';

import { IHotelModelInstance } from '../models';
import { ConfigServiceClient } from '../services/hotel-config.service';

type Response = IHotelModelInstance | undefined;

// We want to fetch more regularly if we're in development, for previewing content
const staleTime = process.env.NODE_ENV === 'development' ? 10e3 : 120e3;

const getHotelCacheKey = (
  hotelReferenceId: string | undefined,
  locale: string
) => {
  return ['hotel', hotelReferenceId, locale];
};

export const useHotelQuery = (
  hotelReferenceId: string | undefined,
  options?: UseQueryOptions<Response>
) => {
  const kongClientConfig = useKongEnnismoreApiClientConfig();
  const awsApiGatewayClientConfig = useAwsApiGatewayEnnismoreApiClientConfig();
  const locale = useLocale();
  const awsGatewayFlag = trpc.flags.evaluate.useQuery({
    key: 'feature-api-gateway-em-config',
    hotelReferenceId: hotelReferenceId,
  });
  const awsGatewayFlagEnabled = awsGatewayFlag.data;

  return useQuery<Response>(
    [
      ...getHotelCacheKey(hotelReferenceId, locale.baseName),
      awsGatewayFlagEnabled,
    ],
    async () => {
      if (!hotelReferenceId || !awsGatewayFlag.isFetched) return undefined;

      const client = new ConfigServiceClient(
        awsGatewayFlagEnabled ? awsApiGatewayClientConfig : kongClientConfig
      );

      return await client.getHotelConfig({
        hotelReferenceId,
        locale: locale.baseName,
      });
    },
    {
      suspense: false,
      staleTime,
      retry: 2,
      ...options,
    }
  );
};

export const useHotel = (
  hotelReferenceId: string | undefined,
  options?: UseQueryOptions<Response>
) => {
  const response = useHotelQuery(hotelReferenceId, options);
  return response.data;
};
